import { Target, Lightbulb, ShieldCheck, TrendingUp } from 'lucide-react';

export default function AboutUs() {
    const values = [
        {
            title: "Our Mission",
            desc: "To empower every professional with the tools and confidence to land their dream role.",
            icon: <Target className="text-brand-primary" size={24} />,
            bg: "bg-brand-light"
        },
        {
            title: "Expert Led",
            desc: "We combine industry-leading ATS technology with human career expertise.",
            icon: <Lightbulb className="text-brand-accent" size={24} />,
            bg: "bg-pink-50"
        },
        {
            title: "Trust & Integrity",
            desc: "Your data and career goals are handled with the highest level of security.",
            icon: <ShieldCheck className="text-brand-dark" size={24} />,
            bg: "bg-purple-100"
        },
        {
            title: "Growth Focused",
            desc: "We don't just find jobs; we build long-term career growth paths.",
            icon: <TrendingUp className="text-brand-primary" size={24} />,
            bg: "bg-brand-light"
        }
    ];

    // ✅ Smooth scroll with navbar offset
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        const offset = 80;

        if (element) {
            const top = element.offsetTop - offset;
            window.scrollTo({
                top,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="relative min-h-screen bg-brand-light text-slate-900 overflow-hidden">

            {/* GLOBAL BACKGROUND */}
            <div className="absolute inset-0 bg-brand-radial -z-10"></div>

            {/* HERO */}
            <section className="pt-32 pb-20 text-center px-6">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-5xl md:text-6xl font-extrabold mb-6 tracking-tight">
                        We are{" "}
                        <span className="bg-brand-gradient bg-clip-text text-transparent">
                            Genius Bridge Technologies
                        </span>
                    </h1>

                    <p className="text-xl text-slate-600 leading-relaxed">
                        Based in Wyoming, USA, we are a team of tech enthusiasts and career coaches
                        dedicated to redefining how professionals enter the tech industry.
                        We believe talent is universal, but opportunity is not.
                    </p>
                </div>
            </section>

            {/* STORY */}
            <section className="py-20 px-6 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12 items-center">

                    <div className="relative">
                        <img
                            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                            alt="team"
                            className="rounded-3xl shadow-2xl relative z-10"
                        />

                        <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-brand-primary/20 rounded-full -z-0"></div>
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-3xl font-bold">Why We Started</h2>

                        <p className="text-slate-600 text-lg">
                            Genius Bridge Technologies was founded on a simple realization: many brilliant engineers
                            and designers were being filtered out by automated systems before even
                            getting a chance to speak.
                        </p>

                        <p className="text-slate-600 text-lg">
                            We built a platform that combines <strong>ATS-Optimization</strong> with
                            <strong>Personal Mentorship</strong>. Since our launch, we have helped
                            over 2,000 professionals bridge the gap to their next big opportunity.
                        </p>

                        <button onClick={() => scrollToSection('contact')} className="bg-brand-dark text-white px-8 py-3 rounded-xl font-semibold hover:bg-brand-primary transition-all">
                            Meet the Founders
                        </button>
                    </div>
                </div>
            </section>



            {/* VALUES */}
            <section className="py-24 px-6">
                <div className="max-w-7xl mx-auto">

                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">
                            The Values That Drive Us
                        </h2>

                        <div className="w-20 h-1.5 bg-brand-gradient mx-auto rounded-full"></div>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((val, i) => (
                            <div
                                key={i}
                                className="bg-white/70 backdrop-blur-xl p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-white/40"
                            >
                                <div className={`w-12 h-12 ${val.bg} rounded-xl flex items-center justify-center mb-6`}>
                                    {val.icon}
                                </div>

                                <h3 className="text-xl font-bold mb-3">
                                    {val.title}
                                </h3>

                                <p className="text-slate-600 text-sm leading-relaxed">
                                    {val.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-6">
                <div className="max-w-5xl mx-auto bg-brand-gradient rounded-[2.5rem] p-12 text-center text-white relative overflow-hidden shadow-2xl">

                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            Ready to cross the bridge?
                        </h2>

                        <p className="text-white/80 mb-10 text-lg max-w-xl mx-auto">
                            Join thousands of professionals who have accelerated their career with our support.
                        </p>

                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button onClick={() => scrollToSection('contact')} className="bg-white text-brand-primary px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition">
                                Get Started Today
                            </button>

                            <button onClick={() => scrollToSection('careers')} className="bg-white/10 border border-white/30 px-8 py-4 rounded-xl font-bold hover:bg-white/20 transition">
                                View Open Roles
                            </button>
                        </div>
                    </div>

                    {/* Background Glow */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full -mr-20 -mt-20"></div>
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full -ml-10 -mb-10"></div>
                </div>
            </section>
        </div>
    );
}